/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {ENTER} from '@angular/material/core';
import {MdChipList} from './chip-list';

export interface MdChipInputEvent {
  input: HTMLInputElement;
  value: string;
}
/**
 * Directive that adds chip-specific behaviors to an input element inside <md-form-field>.
 * May be placed inside or outside of an <md-chip-list>.
 */
export class MdChipInput {
  focused: boolean = false;
  _chipList: MdChipList;
/**
 * Register input for chip list
 * @param {?} value
 * @return {?}
 */
set chipList(value: MdChipList) {
    if (value) {
      this._chipList = value;
      this._chipList.registerInput(this);
    }
  }
/**
 * Whether or not the chipEnd event will be emitted when the input is blurred.
 * @return {?}
 */
get addOnBlur() { return this._addOnBlur; }
/**
 * @param {?} value
 * @return {?}
 */
set addOnBlur(value) { this._addOnBlur = coerceBooleanProperty(value); }
  _addOnBlur: boolean = false;

  /**
   * The list of key codes that will trigger a chipEnd event.
   *
   * Defaults to `[ENTER]`.
   */
  // TODO(tinayuangao): Support Set here
   separatorKeyCodes: number[] = [ENTER];
/**
 * Emitted when a chip is to be added.
 */
chipEnd = new EventEmitter<MdChipInputEvent>();

   _matChipInputTokenEnd = this.chipEnd;
/**
 * @param {?} value
 * @return {?}
 */
set matChipList(value: MdChipList) { this.chipList = value; }
/**
 * @return {?}
 */
get matAddOnBlur() { return this._addOnBlur; }
/**
 * @param {?} value
 * @return {?}
 */
set matAddOnBlur(value) { this.addOnBlur = value; }
/**
 * @return {?}
 */
get matSeparatorKeyCodes() { return this.separatorKeyCodes; }
/**
 * @param {?} v
 * @return {?}
 */
set matSeparatorKeyCodes(v: number[]) { this.separatorKeyCodes = v; }

   placeholder: string = '';
/**
 * @return {?}
 */
get empty(): boolean {
    let /** @type {?} */ value: string | null = this._inputElement.value;
    return value == null || value === '';
  }
/**
 * The native input element to which this directive is attached.
 */
protected _inputElement: HTMLInputElement;
/**
 * @param {?} _elementRef
 */
constructor(protected _elementRef: ElementRef) {
    this._inputElement = this._elementRef.nativeElement as HTMLInputElement;
  }
/**
 * Utility method to make host definition/tests more clear.
 * @param {?=} event
 * @return {?}
 */
_keydown(event?: KeyboardEvent) {
    this._emitChipEnd(event);
  }
/**
 * Checks to see if the blur should emit the (chipEnd) event.
 * @return {?}
 */
_blur() {
    if (this.addOnBlur) {
      this._emitChipEnd();
    }
    this.focused = false;
    // Blur the chip list if it is not focused
    if (!this._chipList.focused) {
      this._chipList._blur();
    }
    this._chipList.stateChanges.next();
  }
/**
 * @return {?}
 */
_focus() {
    this.focused = true;
    this._chipList.stateChanges.next();
  }
/**
 * Checks to see if the (chipEnd) event needs to be emitted.
 * @param {?=} event
 * @return {?}
 */
_emitChipEnd(event?: KeyboardEvent) {
    if (!this._inputElement.value && !!event) {
      this._chipList._keydown(event);
    }
    if (!event || this.separatorKeyCodes.indexOf(event.keyCode) > -1) {
      this.chipEnd.emit({ input: this._inputElement, value: this._inputElement.value });

      if (event) {
        event.preventDefault();
      }
    }
  }
/**
 * @return {?}
 */
focus() { this._inputElement.focus(); }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'input[mdChipInputFor], input[matChipInputFor]',
  host: {
    'class': 'mat-chip-input mat-input-element',
    '(keydown)': '_keydown($event)',
    '(blur)': '_blur()',
    '(focus)': '_focus()',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'chipList': [{ type: Input, args: ['mdChipInputFor', ] },],
'addOnBlur': [{ type: Input, args: ['mdChipInputAddOnBlur', ] },],
'separatorKeyCodes': [{ type: Input, args: ['mdChipInputSeparatorKeyCodes', ] },],
'chipEnd': [{ type: Output, args: ['mdChipInputTokenEnd', ] },],
'_matChipInputTokenEnd': [{ type: Output, args: ['matChipInputTokenEnd', ] },],
'matChipList': [{ type: Input, args: ['matChipInputFor', ] },],
'matAddOnBlur': [{ type: Input, args: ['matChipInputAddOnBlur', ] },],
'matSeparatorKeyCodes': [{ type: Input, args: ['matChipInputSeparatorKeyCodes', ] },],
'placeholder': [{ type: Input },],
};
}

function MdChipInput_tsickle_Closure_declarations() {
/** @type {?} */
MdChipInput.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdChipInput.ctorParameters;
/** @type {?} */
MdChipInput.propDecorators;
/** @type {?} */
MdChipInput.prototype.focused;
/** @type {?} */
MdChipInput.prototype._chipList;
/** @type {?} */
MdChipInput.prototype._addOnBlur;
/** @type {?} */
MdChipInput.prototype.separatorKeyCodes;
/**
 * Emitted when a chip is to be added.
 * @type {?}
 */
MdChipInput.prototype.chipEnd;
/** @type {?} */
MdChipInput.prototype._matChipInputTokenEnd;
/** @type {?} */
MdChipInput.prototype.placeholder;
/**
 * The native input element to which this directive is attached.
 * @type {?}
 */
MdChipInput.prototype._inputElement;
/** @type {?} */
MdChipInput.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
